import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '@capsico/ui';
import type { CountryCode, CustomerAddonGroup, CustomerDish } from '@capsico/contracts';
import { useI18n } from '../i18n';
import { useDeliveryPoint } from '../hooks/useDeliveryPoint';
import { useRestaurant, type MenuRow } from '../hooks/useRestaurant';
import { useCustomise } from '../hooks/useCustomise';
import { usePublicReviews } from '../hooks/useReviews';
import { hygieneLabel, publicReviewView, summaryCopy } from '../i18n/reviewLabels';
import {
  HygieneBadge,
  PhotoViewer,
  PublicReviewCard,
  RatingSummaryCard,
} from '../components/Reviews';
import { lineFor, useAddToCart, type CustomisedChoice } from '../hooks/useAddToCart';
import { cartSummary } from '../hooks/useCart';
import { costForTwo, money } from '../utils/money';
import { distanceText, etaText, ratingText } from '../utils/format';
import { DishCard, DISH_ROW_HEIGHT } from '../components/DishCard';
import { CustomiseSheet } from '../components/CustomiseSheet';
import { VirtualList } from '../components/VirtualList';
import { CartBar, ConfirmDialog } from '../components/CartBits';
import { Button, EmptyState, IconButton, ListSkeleton, Notice, Toggle } from '../components/ui';

const CATEGORY_ROW_HEIGHT = 52;

/**
 * Restaurant page (C6): cover, name, cuisines, rating · ETA · distance · for two, licence line,
 * not-deliverable / closed notice, veg-only switch, in-menu search, then the menu as category
 * headers + C2 dish rows (windowed past 100 rows). ADD opens the C3 sheet when customisable.
 */
export function RestaurantPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { outletId = '' } = useParams();
  const point = useDeliveryPoint();
  const r = useRestaurant(outletId, point);
  const outletRef = r.page
    ? { id: r.page.outlet.outletId, name: r.page.outlet.name, country: r.page.outlet.country }
    : null;
  const cartFlow = useAddToCart(outletRef, { added: t('cart.added'), full: t('cart.full') });
  const reviews = usePublicReviews(outletId);
  const [photo, setPhoto] = useState<string | null>(null);

  const back = (
    <IconButton
      icon="chevron-left"
      label={t('common.back')}
      onClick={() => navigate(-1)}
      className="bg-surface/90 shadow"
    />
  );

  if (!point) {
    return (
      <div className="min-h-dvh bg-page">
        <div className="px-2 pt-[max(env(safe-area-inset-top),8px)]">{back}</div>
        <EmptyState
          icon="pin"
          title={t('location.choose')}
          action={<Button onClick={() => navigate('/')}>{t('location.choose')}</Button>}
        >
          {t('location.setFirst')}
        </EmptyState>
      </div>
    );
  }
  if (r.loading) {
    return (
      <div className="min-h-dvh bg-page">
        <div className="aspect-video w-full animate-pulse bg-tile" />
        <div className="px-4 pt-4">
          <ListSkeleton rows={4} />
        </div>
      </div>
    );
  }
  if (r.notFound || r.failed || !r.page) {
    return (
      <div className="min-h-dvh bg-page">
        <div className="px-2 pt-[max(env(safe-area-inset-top),8px)]">{back}</div>
        {r.notFound ? (
          <EmptyState
            icon="store"
            title={t('restaurant.notFound')}
            action={<Button onClick={() => navigate('/')}>{t('nav.home')}</Button>}
          />
        ) : (
          <div className="px-4 pt-4">
            <Notice
              tone="negative"
              supportRef
              title={t('common.wrong')}
              action={
                <Button size="sm" tone="outline" onClick={r.retry}>
                  {t('common.retry')}
                </Button>
              }
            />
          </div>
        )}
      </div>
    );
  }

  const { outlet, menu } = r.page;
  const country = outlet.country;
  const rating = ratingText(outlet.rating);
  const forTwo = costForTwo(country, outlet.costForTwoMinor);
  const canAdd = outlet.open && outlet.deliverable;
  const hygiene = outlet.hygieneBadge ?? reviews.summary?.hygiene ?? null;
  const preview = reviews.rows.slice(0, 2);

  const add = (dish: CustomerDish) => {
    if (dish.customisable) r.openDish(dish);
    else
      cartFlow.add(
        lineFor(dish, menu.addonGroups, {
          variantId: null,
          addonOptionIds: [],
          comboPicks: [],
          quantity: 1,
        }),
      );
  };
  const summary = cartSummary(cartFlow.cart);
  const cartHere = cartFlow.cart !== null;

  return (
    <div className={`min-h-dvh bg-surface ${cartHere ? 'pb-24' : 'pb-10'}`}>
      <div className="relative aspect-video w-full bg-brand-soft">
        {outlet.coverLargeUrl || outlet.coverUrl ? (
          <img
            src={outlet.coverLargeUrl ?? outlet.coverUrl ?? ''}
            alt=""
            className="size-full object-cover"
          />
        ) : (
          <span className="flex size-full items-center justify-center text-5xl font-bold text-brand-ink">
            {outlet.name.slice(0, 1)}
          </span>
        )}
        <div className="absolute top-0 left-0 px-2 pt-[max(env(safe-area-inset-top),8px)]">
          {back}
        </div>
      </div>

      <div className="relative -mt-6 rounded-t-sheet bg-surface px-4 pt-4">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-bold text-ink">{outlet.name}</h1>
            <p className="mt-0.5 truncate text-xs text-ink-2">{outlet.cuisines.join(', ')}</p>
            <p className="mt-0.5 truncate text-xs text-ink-3">{outlet.locality}</p>
          </div>
          {!rating && (outlet.isNew || reviews.summary?.isNew) && (
            <span className="shrink-0 rounded-lg bg-brand px-2.5 py-1 text-sm font-bold text-on-brand">
              {t('reviews.new')}
            </span>
          )}
          {rating && (
            <div className="flex shrink-0 flex-col items-center rounded-lg bg-positive px-2 py-1 text-white">
              <span className="inline-flex items-center gap-0.5 text-sm font-bold tabular">
                {rating}
                <Icon name="star" size={12} />
              </span>
              <span className="text-micro tabular opacity-90">{outlet.rating.count}</span>
            </div>
          )}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-2 tabular">
          <span className="inline-flex items-center gap-1">
            <Icon name="clock" size={14} />
            {etaText(outlet.eta)}
          </span>
          <span className="inline-flex items-center gap-1">
            <Icon name="map" size={14} />
            {distanceText(outlet.distanceKm)}
          </span>
          {forTwo && <span>{t('home.forTwo', { amount: forTwo })}</span>}
          {outlet.localLegend && (
            <span className="rounded-md bg-brand px-1.5 py-0.5 text-micro font-semibold text-on-brand">
              {t('home.legend')}
            </span>
          )}
        </div>
        {(outlet.licence || hygiene) && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {hygiene && <HygieneBadge tone={hygiene} label={hygieneLabel(t, hygiene)} />}
            {outlet.licence && (
              <p className="inline-flex items-center gap-1 text-caption text-ink-3">
                <Icon name="shield" size={12} />
                {t('restaurant.licence', {
                  kind: outlet.licence.kind,
                  number: outlet.licence.number,
                })}
              </p>
            )}
          </div>
        )}

        {(!outlet.open || !outlet.deliverable) && (
          <div className="mt-3">
            <Notice
              tone="warning"
              title={
                !outlet.open
                  ? (outlet.closedReason ?? t('restaurant.closed'))
                  : (outlet.notDeliverableReason ?? t('address.notServed'))
              }
            />
          </div>
        )}

        {reviews.summary && !reviews.unavailable && (
          <section className="mt-4 space-y-3">
            <RatingSummaryCard
              {...summaryCopy(t, reviews.summary)}
              hygiene={null}
              action={
                reviews.rows.length > 0 ? (
                  <Button
                    size="sm"
                    tone="outline"
                    full
                    onClick={() => navigate(`/r/${outlet.outletId}/reviews`)}
                  >
                    {t('reviews.seeAll')}
                  </Button>
                ) : null
              }
            />
            {preview.map((review) => (
              <PublicReviewCard
                key={review.id}
                review={publicReviewView(t, review)}
                photoLabel={t('reviews.photo')}
                onPhoto={setPhoto}
              />
            ))}
          </section>
        )}

        <div className="mt-4 flex items-center gap-3">
          <label className="flex h-10 min-w-0 flex-1 items-center gap-2 rounded-xl bg-tile px-3">
            <Icon name="search" size={16} />
            <input
              type="search"
              value={r.text}
              onChange={(event) => r.setText(event.target.value)}
              placeholder={t('restaurant.searchMenu')}
              aria-label={t('restaurant.searchMenu')}
              className="min-w-0 flex-1 bg-transparent text-sm text-ink placeholder:text-ink-3 focus:outline-none"
            />
          </label>
          <div className="flex shrink-0 items-center gap-2">
            <span className="text-xs font-medium text-ink-2">{t('restaurant.vegOnly')}</span>
            <Toggle on={r.vegOnly} onChange={r.setVegOnly} label={t('restaurant.vegOnly')} />
          </div>
        </div>
      </div>

      <div className="mt-2">
        {r.rows.length === 0 ? (
          <EmptyState
            icon="utensils"
            title={r.text ? t('restaurant.noMatch', { query: r.text }) : t('restaurant.menuEmpty')}
          />
        ) : (
          <VirtualList<MenuRow>
            items={r.rows}
            rowHeight={(row) => (row.kind === 'category' ? CATEGORY_ROW_HEIGHT : DISH_ROW_HEIGHT)}
            getKey={(row) => row.key}
            renderRow={(row) =>
              row.kind === 'category' ? (
                <div className="flex h-full items-end justify-between bg-page px-4 pb-2">
                  <h2 className="truncate text-base font-bold text-ink">{row.name}</h2>
                  <span className="text-xs text-ink-3 tabular">
                    {t('restaurant.items', { count: row.count })}
                  </span>
                </div>
              ) : (
                <div className="h-full shadow-[inset_0_-1px_0_var(--color-hair)]">
                  <DishCard
                    dish={row.dish}
                    country={country}
                    labels={{
                      bestseller: t('restaurant.bestseller'),
                      customisable: t('restaurant.customisable'),
                      add: t('restaurant.add'),
                      likes: (count) => t('restaurant.likes', { count }),
                    }}
                    disabled={!canAdd}
                    quantity={
                      cartFlow.cart?.outletId === outlet.outletId
                        ? cartFlow.quantity(row.dish.id)
                        : 0
                    }
                    onAdd={() => add(row.dish)}
                    onRemove={() => cartFlow.removeOne(row.dish.id)}
                  />
                </div>
              )
            }
          />
        )}
      </div>

      {r.open && (
        <Customise
          key={r.open.id}
          dish={r.open}
          addonGroups={menu.addonGroups}
          country={country}
          onClose={r.closeDish}
          onAdd={(choice) => {
            cartFlow.add(lineFor(r.open as CustomerDish, menu.addonGroups, choice));
            r.closeDish();
          }}
        />
      )}

      {cartFlow.cart && (
        <CartBar
          countText={
            summary.count === 1 ? t('cart.item') : t('cart.items', { count: summary.count })
          }
          amountText={money(cartFlow.cart.country, summary.estimateMinor)}
          outletName={cartFlow.cart.outletId === outlet.outletId ? null : cartFlow.cart.outletName}
          actionLabel={t('cart.view')}
          aboveNav={false}
          onOpen={() => navigate('/cart')}
        />
      )}

      {photo && (
        <PhotoViewer url={photo} closeLabel={t('common.back')} onClose={() => setPhoto(null)} />
      )}

      {cartFlow.pending && cartFlow.otherRestaurant && (
        <ConfirmDialog
          title={t('cart.replaceTitle')}
          body={t('cart.replaceBody', { name: cartFlow.otherRestaurant })}
          confirmLabel={t('cart.replaceYes')}
          cancelLabel={t('cart.replaceNo')}
          onConfirm={cartFlow.confirmReplace}
          onCancel={cartFlow.cancelReplace}
        />
      )}
    </div>
  );
}

function Customise({
  dish,
  addonGroups,
  country,
  onClose,
  onAdd,
}: {
  dish: CustomerDish;
  addonGroups: CustomerAddonGroup[];
  country: CountryCode;
  onClose: () => void;
  onAdd: (choice: CustomisedChoice) => void;
}) {
  const { t } = useI18n();
  const c = useCustomise(dish, addonGroups);
  return (
    <CustomiseSheet
      dish={dish}
      country={country}
      labels={{
        size: t('customise.size'),
        required: t('customise.required'),
        optional: t('customise.optional'),
        rule: ({ min, max }) =>
          min === max
            ? t('customise.exactly', { count: min })
            : min === 0
              ? t('customise.upTo', { max })
              : t('customise.between', { min, max }),
        pick: (count) => t('customise.pick', { count }),
        addToCart: (price) => t('customise.addToCart', { price }),
        incomplete: t('customise.incomplete'),
        close: t('common.cancel'),
      }}
      variantId={c.variantId}
      onVariant={c.setVariantId}
      groups={c.groups}
      onPick={c.togglePick}
      slots={c.comboSlots}
      onSlot={c.toggleSlot}
      quantity={c.quantity}
      onIncrease={c.increase}
      onDecrease={c.decrease}
      total={c.total}
      complete={c.complete}
      onAdd={() =>
        onAdd({
          variantId: c.variantId,
          addonOptionIds: c.groups.flatMap((rule) => rule.chosen),
          comboPicks: c.comboSlots.map((slot) => ({ slotId: slot.id, itemIds: slot.chosen })),
          quantity: c.quantity,
        })
      }
      onClose={onClose}
    />
  );
}
